import {
    reactRouterV4Instrumentation,
    init,
    captureException,
    configureScope,
    Scope,
    createReduxEnhancer,
    makeFetchTransport,
    BrowserTracing,
} from '@sentry/react';
import {makeMultiplexedTransport, ModuleMetadata} from '@sentry/core';

import {ENVIRONMENT, RELEASE_VERSION} from 'constants/app';

const EXTRA_KEY = 'ROUTE_TO';

// @ts-expect-error
const transport = makeMultiplexedTransport(makeFetchTransport, args => {
    const event = args.getEvent();
    if (event && event.extra && EXTRA_KEY in event.extra && Array.isArray(event.extra[EXTRA_KEY])) {
        return event.extra[EXTRA_KEY];
    }
    return [];
});

/**
 * Init the Sentry browser SDK
 */
export function initSentry({history}: {history: History}) {
    init({
        dsn: 'https://89c787ecff7749e3beafaf06ada196d5@o100932.ingest.sentry.io/4504678674661376',
        transport,
        environment: ENVIRONMENT,
        release: RELEASE_VERSION,
        sampleRate: 0.05,
        tracesSampleRate: 0.05,
        // By default Sentry SDKs normalize any context to a depth of 3
        // We increase it for integration with redux, as in the example from the documentation:
        // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/redux/#normalization-depth
        normalizeDepth: 5,
        integrations: [
            new ModuleMetadata(),
            new BrowserTracing({
                routingInstrumentation: reactRouterV4Instrumentation(history),
            }),
        ],
        tracePropagationTargets: ['localhost', /^\/backend\//, /^\/ms\//, /^\/bff\//, /^\/mfe-/],
        beforeSend: event => {
            const convertedEvent = {...event};
            if (event?.exception?.values && event?.exception?.values[0].stacktrace?.frames) {
                const {frames} = event.exception.values[0].stacktrace;
                // Find the last frame with module metadata containing a DSN
                const routeTo = frames.find(
                    frame => frame.module_metadata && frame.module_metadata.dsn,
                )?.module_metadata.dsn;

                if (routeTo) {
                    convertedEvent.extra = {
                        ...convertedEvent.extra,
                        [EXTRA_KEY]: [routeTo],
                    };
                }
            }

            return convertedEvent;
        },
    });
}
/**
 * Creates Sentry's Redux enhancer to
 * https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/redux/
 *
 * Note from docs:
 * Don't pass it to applyMiddleware and don't call the method when you pass it to the createStore method.
 * @returns {any} - Redux enhancer
 */
export const createSentryReduxEnhancer = () => {
    return createReduxEnhancer();
};

/**
 * Send error to Sentry
 * @param {Error} error - Error object
 * @param {string | {string: any}} [extras=''] - Additional details
 */
export function logWithSentry(error: Error, extras: string | {} = '') {
    configureScope((scope: Scope) => {
        scope.setContext('additionalDetails', {details: extras});
        captureException(error);
    });
}
